import React, { useContext, useEffect, useRef, useState } from "react";
import { Col, Row, Button, Space, Tabs, Input, Popconfirm } from "antd";
import { EnableWeightToggle } from "./PickFeeSection.styles";
import { FormSection } from "components/ratecard/_common";
import Form, { FormInstance } from "antd/lib/form";
import { useEditServiceContext } from "components/ratecard/contextProviders";
import Title from "antd/lib/typography/Title";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { isEqual } from "lodash-es";
import {
  ALPHABET_ARRAY,
  INPUT_NEEDED,
  notifyAlert,
  notifyError,
  pickPackRateTemplate,
  sanitazePickPackRate,
} from "./Helpers";
import { PickPackRate, PickTierLine, VolumeTierLine, WeightTierLine } from "../interfeces";
import VolumeTier from "./VolumeTier";
const EditableContext = React.createContext<FormInstance<any> | null>(null);

const { TabPane } = Tabs;

const PickFeeSection: React.FC<{ form: FormInstance }> = ({ form }) => {
  const [weightBased, setWeightBased] = React.useState(true);
  const [pickPackRate, setPickPackRate] = React.useState(pickPackRateTemplate);
  const { pickFee: pickFeeSaved } = form.getFieldsValue();

  const isPickFeeEmpty = (pickFeeSaved: typeof pickPackRateTemplate) => {
    return !(pickFeeSaved && pickFeeSaved.weights && Object.keys(pickFeeSaved).length !== 0);
  };

  if (!isPickFeeEmpty(pickFeeSaved)) {
    const isDeletable = (index: number, length: number) => {
      return index > 0 && index === length - 1;
    };

    const pickPackRatePrevState: PickPackRate = {
      weights: pickFeeSaved.weights.map((w: WeightTierLine, indexWeight: number) => ({
        ...w,
        to: w.to === null ? "+" : w.to,
        key: indexWeight + 1,
        deletable: isDeletable(indexWeight, pickFeeSaved.weights.length),
        tiers: w.tiers.map((t: VolumeTierLine, indexVolumes: number) => ({
          ...t,
          to: t.to === null ? "+" : t.to,
          key: indexVolumes + 1,
          deletable: isDeletable(indexVolumes, w.tiers.length),
          picks: t.picks.map((p: PickTierLine, indexPick: number) => ({
            ...p,
            to: p.to === null ? "+" : p.to,
            key: indexPick + 1,
            deletable: isDeletable(indexPick, t.picks.length),
          })),
        })),
      })),
      description: pickPackRate.description,
    };

    if (!isEqual(pickPackRatePrevState, pickPackRate)) {
      setWeightBased(pickPackRatePrevState.weights.length === 1);
      setPickPackRate(pickPackRatePrevState);
    }
  }

  const { allowEditing, saveValidationFailed } = useEditServiceContext();

  const updatePickPackRate = (pickPackRate: typeof pickPackRateTemplate) => {
    if (!allowEditing) {
      return;
    }
    const pickFee = sanitazePickPackRate(pickPackRate);
    form.setFieldsValue({ pickFee: null });
    form.setFieldsValue({ pickFee });
    setPickPackRate({ ...pickPackRate });
  };

  const getDataSource = (weightTier: number, volumeTier: number) => {
    const tiersData = pickPackRate.weights
      .find((wt) => wt.key === weightTier)
      ?.tiers.find((vt) => vt.key === volumeTier)?.picks;

    const dataSource = [];

    if (tiersData) {
      for (const tier of tiersData) {
        dataSource.push({
          key: tier.key,
          from: tier.from,
          to: tier.to,
          price: tier.price,
          weight_tier: weightTier,
          volume_tier: volumeTier,
          deletable: tier.deletable,
        });
      }
    }

    return dataSource;
  };

  const handleAddTableTier = (weightTier: number, volumeTier: number) => {
    const volumeTierObj = pickPackRate.weights
      .find((wt) => wt.key === weightTier)
      ?.tiers.find((vt) => vt.key === volumeTier);

    if (volumeTierObj) {
      const invalidTiers = volumeTierObj.picks.filter((p) => p.from === 1);

      if (invalidTiers.length > 1) {
        notifyError("You need to set the Upper for the previous tier");
        return;
      }

      volumeTierObj.picks = volumeTierObj.picks.map((p) => ({
        ...p,
        deletable: false,
        to: p.to === "+" ? INPUT_NEEDED : p.to,
      }));

      volumeTierObj.picks.push({
        key: volumeTierObj.picks.length + 1,
        from: 1,
        to: "+",
        price: 0,
        deletable: true,
      });

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleDeteleTableTier = (key: number, weightTier: number, volumeTier: number) => {
    const volumeTierObj = pickPackRate.weights
      .find((wt) => wt.key === weightTier)
      ?.tiers.find((vt: any) => vt.key === volumeTier);

    if (volumeTierObj) {
      if (key < volumeTierObj.picks.length) {
        notifyError("Only the last tier can be deleted");
        return;
      }
      volumeTierObj.picks = volumeTierObj.picks.filter((p) => p.key !== key);
      const prevTier = volumeTierObj.picks.find((p) => p.key === key - 1);
      if (prevTier) {
        prevTier.to = "+";
        if (prevTier.key > 1) {
          prevTier.deletable = true;
        }
      }

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleSaveTableTier = (key: number, weightTier: number, volumeTier: number, to: number, price: number) => {
    const volumeTierObj = pickPackRate.weights
      .find((wt) => wt.key === weightTier)
      ?.tiers.find((vt) => vt.key === volumeTier);

    if (volumeTierObj) {
      const tierForRelace = volumeTierObj.picks.find((p) => p.key === key);
      if (tierForRelace) {
        if (price.toString() !== "") {
          tierForRelace.price = price;
        }

        if (volumeTierObj.picks.length > key) {
          const nextTier = volumeTierObj.picks.find((p) => p.key === key + 1);
          if (nextTier) {
            const nextTierMax = !isNaN(+nextTier.to) ? +nextTier.to : Infinity;

            if (+to >= +tierForRelace.from && +to < nextTierMax) {
              tierForRelace.to = "" + to;
              nextTier.from = +to + 1;
            } else if (+nextTier.from > +tierForRelace.from) {
              notifyAlert(
                "Upper value must be higher than the Lower for the current tier and lower than the next tier Upper"
              );
              tierForRelace.to = nextTier.from - 1 + "";
            } else {
              notifyAlert("Upper value must be higher than the Lower");
              tierForRelace.to = INPUT_NEEDED;
            }
          }
        }
        updatePickPackRate({ ...pickPackRate });
      }
    }
  };

  let columns = [
    {
      title: "Pick (Lower)",
      dataIndex: "from",
      key: "pick_lower",
    },
    {
      title: "Pick (Upper)",
      dataIndex: "to",
      key: "to",
      editable: allowEditing,
      width: "30%",
      className: "ant-editable-field",
      required: false,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      editable: allowEditing,
      width: "30%",
      className: "ant-editable-field",
      required: true,
    },
    {
      dataIndex: "operation",
      // eslint-disable-next-line react/display-name
      render: (
        _: any,
        record: {
          deletable: boolean;
          key: number;
          weight_tier: number;
          volume_tier: number;
        }
      ) => {
        if (allowEditing && record.deletable) {
          return (
            <Popconfirm
              title="Are you sure to delete?"
              onConfirm={() => handleDeteleTableTier(record.key, record.weight_tier, record.volume_tier)}
            >
              <Button icon={<DeleteOutlined />} size="small" />
            </Popconfirm>
          );
        }
        return null;
      },
    },
  ];

  columns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave: (row: any) => {
          handleSaveTableTier(row.key, row.weight_tier, row.volume_tier, row.to, row.price);
        },
      }),
    };
  });

  const EditableRow = ({ ...props }) => {
    const [form] = Form.useForm();
    return (
      <Form form={form} component={false}>
        <EditableContext.Provider value={form}>
          <tr {...props} />
        </EditableContext.Provider>
      </Form>
    );
  };

  const EditableCell = ({ ...props }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef<any>(null);
    const form = useContext<any>(EditableContext);
    useEffect(() => {
      if (editing) {
        inputRef.current.focus();
      }
    }, [editing]);

    const toggleEdit = () => {
      let fieldValue = props.record[props.dataIndex];
      if (fieldValue === "+") {
        return;
      }
      setEditing(!editing);
      if (fieldValue === 0 || fieldValue === INPUT_NEEDED) {
        fieldValue = "";
      }
      form.setFieldsValue({
        [props.dataIndex]: fieldValue,
      });
    };

    const save = async () => {
      try {
        const values = await form.validateFields();
        toggleEdit();
        props.handleSave({ ...props.record, ...values });
      } catch (errInfo) {
        console.log("Save failed:", errInfo);
      }
    };

    let childNode = props.children;
    const remarkError = saveValidationFailed && childNode[1] === INPUT_NEEDED;
    const disabled = childNode[1] === "+";

    if (props.editable) {
      childNode = editing ? (
        <Form.Item style={{ margin: 0 }} name={props.dataIndex}>
          <Input
            className={remarkError ? "change-needed" : ""}
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            type="number"
          />
        </Form.Item>
      ) : (
        <div
          className={
            "editable-cell-value-wrap " + (remarkError ? "change-needed " : "") + (disabled ? "disabled " : "")
          }
          style={{ paddingRight: 24 }}
          onClick={toggleEdit}
        >
          {props.children}
        </div>
      );
    }

    return <td {...props}>{childNode}</td>;
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const handleAddVolumeTier = (weightTier: number) => {
    const weightTierObj = pickPackRate.weights.find((wt) => wt.key === weightTier);

    if (weightTierObj) {
      const invalidTiers = weightTierObj.tiers.filter((t) => t.from === 1);

      if (invalidTiers.length > 1) {
        notifyError("You need to set the Upper for the previous tier");
        return;
      }

      weightTierObj.tiers = weightTierObj.tiers.map((t) => ({
        ...t,
        deletable: false,
        to: t.to === "+" ? INPUT_NEEDED : t.to,
      }));

      weightTierObj.tiers.push({
        key: weightTierObj.tiers.length + 1,
        from: 1,
        to: "+",
        picks: [
          {
            key: 1,
            from: 1,
            to: "+",
            price: 0,
            deletable: false,
          },
        ],
        deletable: true,
      });

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleUpdateVolumeTier = (weightTier: number, volumeTier: number) => {
    const weightTierObj = pickPackRate.weights.find((wt) => wt.key === weightTier);

    if (weightTierObj) {
      const tierForRelace = weightTierObj.tiers.find((t) => t.key === volumeTier);

      if (volumeTier === weightTierObj.tiers.length) {
        notifyError("Last tier Upper is always unlimited");
        return;
      }

      if (tierForRelace) {
        const to = tierForRelace.to;
        const nextTier = weightTierObj.tiers.find((t) => t.key === volumeTier + 1);

        if (nextTier) {
          const nextTierMax = !isNaN(+nextTier.to) ? +nextTier.to : Infinity;

          if (+to >= +tierForRelace.from && +to < nextTierMax) {
            nextTier.from = +to + 1;
          } else if (nextTier.from > 1) {
            notifyAlert(
              "Upper value must be higher than the Lower for the current tier and lower than the next tier Upper"
            );
            tierForRelace.to = nextTier.from - 1 + "";
          } else {
            notifyAlert("Upper value must be higher than the Lower");
            tierForRelace.to = INPUT_NEEDED;
          }
          updatePickPackRate({ ...pickPackRate });
        }
      }
    }
  };

  const handleDeteleVolumeTier = (weightTier: number, volumeTier: number) => {
    const weightTierObj = pickPackRate.weights.find((wt) => wt.key === weightTier);

    if (weightTierObj) {
      if (volumeTier !== weightTierObj.tiers.length) {
        notifyError("Only the last tier can be deleted");
        return;
      }

      const prevTier = weightTierObj.tiers.find((t) => t.key === volumeTier - 1);

      if (prevTier) {
        prevTier.to = "+";
        if (prevTier.key > 1) {
          prevTier.deletable = true;
        }

        weightTierObj.tiers = weightTierObj.tiers.filter((vt) => vt.key !== volumeTier);

        updatePickPackRate({ ...pickPackRate });
      }
    }
  };

  const handleOnChangeTierThreshold = (value: string, weightKey: number, tierKey: number) => {
    const tier = pickPackRate.weights.find((wt) => wt.key === weightKey)?.tiers.find((t) => t.key === tierKey);
    if (tier) {
      tier.to = value;
      updatePickPackRate({ ...pickPackRate });
    }
  };

  const GetVolumeTier = (weightTier: number) => {
    const volumeTiers = pickPackRate.weights.find((w) => w.key === weightTier)?.tiers;

    const volumeComponents: JSX.Element[] = [];

    if (volumeTiers) {
      volumeTiers.forEach((item, index) => {
        const remarkErrors = saveValidationFailed && item.to === INPUT_NEEDED;
        const deletable = allowEditing && item.deletable;
        const disabled = index === volumeTiers.length - 1;
        volumeComponents.push(
          <VolumeTier
            item={item}
            deletable={deletable}
            disabled={disabled}
            remarkErrors={remarkErrors}
            index={index}
            weightTier={weightTier}
            components={components}
            columns={columns}
            handleDeteleVolumeTier={handleDeteleVolumeTier}
            handleOnChangeTierThreshold={handleOnChangeTierThreshold}
            handleUpdateVolumeTier={handleUpdateVolumeTier}
            handleAddTableTier={handleAddTableTier}
            getDataSource={getDataSource}
          />
        );
      });
    }

    return <>{volumeComponents}</>;
  };

  const handleEditMaxWeight = (weightTierKey: number) => {
    const weightTier = pickPackRate.weights.find((wt) => wt.key === weightTierKey);
    const nextWeightTier = pickPackRate.weights.find((wt) => wt.key === weightTierKey + 1);

    if (weightTier && nextWeightTier) {
      const nextTierMax = !isNaN(+nextWeightTier.to) ? +nextWeightTier.to : Infinity;

      if (+weightTier.to >= weightTier.from && +weightTier.to < nextTierMax) {
        nextWeightTier.from = +(+weightTier.to + 0.1).toFixed(1);
      } else if (nextWeightTier.from > 1) {
        notifyAlert(
          "Upper value must be higher than the Lower for the current tier and lower than the next tier Upper"
        );
        weightTier.to = (nextWeightTier.from - 0.1).toFixed(1);
      } else {
        notifyAlert("Upper value must be higher than the Lower");
        weightTier.to = INPUT_NEEDED;
      }

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleEditWeightLabel = (weightTierKey: number, label: string) => {
    const weightTier = pickPackRate.weights.find((wt) => wt.key === weightTierKey);

    if (weightTier) {
      weightTier.label = label;

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleRepeatedWeightLabel = (weightTierKey: number) => {
    const weightTier = pickPackRate.weights.find((wt) => wt.key === weightTierKey);

    if (weightTier) {
      const repeatedWeightTier = pickPackRate.weights.find(
        (wt) => wt.key !== weightTierKey && wt.label === weightTier.label
      );
      if (repeatedWeightTier) {
        notifyError("The label is being used by another weight tier");
        weightTier.label = "";
      }

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleDeleteWeightTier = (wieghtTierKey: number) => {
    if (pickPackRate.weights.length !== wieghtTierKey || wieghtTierKey === 1) {
      notifyError("Only the last tier can be deleted");
      return;
    }

    const weights = pickPackRate.weights.filter((wt) => wt.key !== wieghtTierKey);
    pickPackRate.weights = weights;
    const wieghtTier = pickPackRate.weights.find((wt) => wt.key === wieghtTierKey - 1);
    if (wieghtTier) {
      wieghtTier.to = "+";
      if (wieghtTierKey > 2) {
        wieghtTier.deletable = true;
      }
    }
    updatePickPackRate({ ...pickPackRate });
  };

  const handleOnChangeWeightLimits = (value: string, weightKey: number) => {
    const weight = pickPackRate.weights.find((wt) => wt.key === weightKey);

    if (weight) {
      weight.to = value;
      updatePickPackRate({ ...pickPackRate });
    }
  };

  const getWeightTiers = () => {
    const tiersData = pickPackRate.weights || [];
    const weightTabComponents: JSX.Element[] = [];

    tiersData.forEach((item) => {
      const remarkError = saveValidationFailed && item.to === INPUT_NEEDED;
      weightTabComponents.push(
        <TabPane
          tab={
            <Space>
              <span>{item.label}</span>
              {allowEditing && item.deletable && (
                <Popconfirm
                  title={`This will delete all ${item.label} data, are you sure?`}
                  onConfirm={() => handleDeleteWeightTier(item.key)}
                >
                  <Button icon={<DeleteOutlined />} size="middle" />
                </Popconfirm>
              )}
            </Space>
          }
          key={item.key}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {!weightBased && (
              <>
                <Row>
                  <Col>
                    <Input
                      placeholder="PickFee Label"
                      style={{ width: 200 }}
                      value={item.label}
                      onChange={(e) => handleEditWeightLabel(item.key, e.target.value)}
                      onBlur={() => handleRepeatedWeightLabel(item.key)}
                      onPressEnter={() => handleRepeatedWeightLabel(item.key)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Title level={3} style={{ margin: "0px;" }}>
                      Weight Limits (lbs)
                    </Title>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Space size="middle">
                      <Input placeholder="1" value={item.from} style={{ width: 150 }} disabled={true} />
                      <Input
                        className={remarkError ? "change-needed" : ""}
                        placeholder={item.to}
                        value={item.to}
                        style={{ width: 150 }}
                        disabled={item.key === tiersData.length}
                        onChange={(e) => handleOnChangeWeightLimits(e.target.value, item.key)}
                        onBlur={() => {
                          handleEditMaxWeight(item.key);
                        }}
                        onPressEnter={() => {
                          handleEditMaxWeight(item.key);
                        }}
                        type="number"
                      />
                    </Space>
                  </Col>
                </Row>
              </>
            )}
            <Row>
              <Col>
                <Space size="large">
                  <Title level={3} style={{ paddingTop: "15px" }}>
                    Volume Tiers
                  </Title>
                  {allowEditing && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={() => handleAddVolumeTier(item.key)}
                    >
                      Add Volume Tier
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
            <Row gutter={16}>{GetVolumeTier(item.key)}</Row>
          </Space>
        </TabPane>
      );
    });

    return (
      <Tabs defaultActiveKey="1" onChange={() => {}}>
        {weightTabComponents}
      </Tabs>
    );
  };

  const handleAddWieghtTier = () => {
    const weights = pickPackRate.weights;
    const prevWeightTier = pickPackRate.weights.find((wt) => wt.key === pickPackRate.weights.length);

    pickPackRate.weights = weights.map((wt) => ({
      ...wt,
      deletable: false,
      to: wt.to === "+" ? INPUT_NEEDED : wt.to,
    }));

    if (prevWeightTier) {
      const letter = ALPHABET_ARRAY[prevWeightTier.key];
      pickPackRate.weights.push({
        key: prevWeightTier.key + 1,
        label: `Weight Tier ${letter}`,
        from: 1,
        to: "+",
        deletable: true,
        tiers: [
          {
            key: 1,
            from: 1,
            to: "+",
            picks: [
              {
                key: 1,
                from: 1,
                to: "+",
                price: 0,
                deletable: false,
              },
            ],
            deletable: false,
          },
        ],
      });

      updatePickPackRate({ ...pickPackRate });
    }
  };

  const handleDisableWeightBased = () => {
    const weights = pickPackRate.weights.filter((wt) => wt.key === 1);
    pickPackRate.weights = weights;
    const weight = pickPackRate.weights.find((wt) => wt.key === 1);

    if (weight) {
      weight.to = "+";
      updatePickPackRate({ ...pickPackRate });
      setWeightBased(true);
    }
  };

  const showNoDataMessage = isPickFeeEmpty(pickFeeSaved) && !allowEditing;
  let weightTitle = "Weight Based";

  if (weightBased) {
    weightTitle = weightTitle + "?";
  }

  return (
    <FormSection title="Pick Fees">
      {showNoDataMessage ? (
        <Row>
          <Col>There is no stored data for Pick Fees, you can add new one.</Col>
        </Row>
      ) : (
        <fieldset disabled={!allowEditing}>
          {allowEditing && (
            <Row className="mt-5" style={{ marginTop: "16px", marginBottom: "16px" }}>
              <Col span={20}>
                <Space>
                  <Title level={3}>{weightTitle}</Title>
                  {weightBased && <EnableWeightToggle onClick={() => setWeightBased(false)}>Enable</EnableWeightToggle>}
                  {!weightBased && (
                    <EnableWeightToggle>
                      <Popconfirm
                        title="This will delete all the other weight tiers, are you sure?"
                        onConfirm={() => handleDisableWeightBased()}
                      >
                        Disable
                      </Popconfirm>
                    </EnableWeightToggle>
                  )}
                </Space>
              </Col>
              <Col span={4}>
                {allowEditing && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    disabled={weightBased}
                    onClick={() => handleAddWieghtTier()}
                  >
                    Add Weight Tier
                  </Button>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <Col span={24}>{getWeightTiers()}</Col>
          </Row>
        </fieldset>
      )}
    </FormSection>
  );
};

export default PickFeeSection;
